import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { getConfigWithToken } from "../../config/config";

const credentialsOptions = [
    "Certified SFBT Coach",
    "LCSW – Licensed Clinical Social Worker",
    "LMSW – Licensed Master Social Worker",
    "LSW – Licensed Social Worker",
    "LICSW – Licensed Independent Clinical Social Worker",
    "LCSW-C – Licensed Certified Social Worker–Clinical",
    "LGSW – Licensed Graduate Social Worker",
    "LPC – Licensed Professional Counselor",
    "LPCC – Licensed Professional Clinical Counselor",
    "LCPC – Licensed Clinical Professional Counselor",
    "LMHC – Licensed Mental Health Counselor",
    "LPC-A – Licensed Professional Counselor Associate",
    "LPCA – Licensed Professional Counselor Associate",
    "LMFT – Licensed Marriage and Family Therapist",
    "LAMFT – Licensed Associate Marriage and Family Therapist",
    "MFT-A – Marriage and Family Therapist Associate",
    "PhD – Licensed Psychologist",
    "PsyD – Licensed Psychologist",
    "MD – Psychiatrist",
    "PMHNP – Psychiatric Mental Health Nurse Practitioner",
    "APRN – Advanced Practice Registered Nurse",
];


const StepReviewCredentials = ({ data, onChange, onNext, loading, error }) => {
    const user = useSelector((state) => state?.user);
    const [isEditing, setIsEditing] = useState(data.credentials.length === 0);
    const [tempCredentials, setTempCredentials] = useState([]);
    const [fetchedStates, setFetchedStates] = useState([]);

    useEffect(() => {
        const fetchStates = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_BASE_URL}/states/all`
                );
                if (response.data.success) {
                    const sorted = response.data.data.sort((a, b) =>
                        (a.name || "").localeCompare(b.name || "")
                    );
                    setFetchedStates(sorted);
                }
            } catch (error) {
                console.error("Error fetching states:", error);
            }
        };

        fetchStates();
    }, []);

    useEffect(() => {
        if (isEditing) {
            setTempCredentials(data.credentials.length > 0 ? [...data.credentials] : [{ state: "", credentials: "", licenseNumber: "", licenseExpiration: "" }]);
        }
    }, [isEditing, data.credentials]);

    const handleAddLicense = () => {
        setTempCredentials([...tempCredentials, { state: "", credentials: "", licenseNumber: "", licenseExpiration: "" }]);
    };

    const handleRemoveLicense = () => {
        if (tempCredentials.length > 1) {
            setTempCredentials(tempCredentials.slice(0, -1));
        }
    };

    const handleTempChange = (index, field, value) => {
        // Prevent years with more than 4 digits for date inputs
        if (field === "licenseExpiration" && value) {
            const year = value.split('-')[0];
            if (year.length > 4) return;
        }
        const updated = [...tempCredentials];
        updated[index] = { ...updated[index], [field]: value };
        setTempCredentials(updated);
    };

    const handleSave = () => {
        // Validate
        const isValid = tempCredentials.every(c => c.state && c.credentials && c.licenseNumber && c.licenseExpiration);
        if (!isValid) {
            alert("Please fill all fields for all licenses.");
            return;
        }
        onChange("credentials", tempCredentials);
        setIsEditing(false);
    };

    const handleCancel = () => {
        if (data.credentials.length > 0) {
            setIsEditing(false);
        } else {
            // If no credentials, maybe just stay here or handle accordingly
            alert("You need at least one license.");
        }
    };

    if (isEditing) {
        return (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {/* Badge Icon */}
                <div style={{ display: "flex", justifyContent: "left", marginBottom: 10 }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="81" height="80" viewBox="0 0 81 80" fill="none">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M40.5 0L29.2455 10.2105L14.067 9.62099L12.0105 24.678L0 33.984L8.1 46.836L4.88249 61.686L19.35 66.3255L26.433 79.767L40.5 74.0205L54.567 79.767L61.65 66.3255L76.1175 61.686L72.9 46.836L81 33.984L68.9895 24.678L66.933 9.62099L51.75 10.2105L40.5 0ZM55.3815 26.0055L61.6185 32.4945L36.306 56.8485L19.314 39.8565L25.677 33.4935L36.432 44.244L55.3815 26.0055Z" fill="#27B99A" />
                    </svg>
                </div>

                <h2 style={{ margin: 0, fontFamily: "Poppins, sans-serif", fontWeight: 700, fontSize: 24, textAlign: "left" }}>
                    Review Credentials
                </h2>

                {tempCredentials.map((license, index) => (
                    <div key={index} style={{ display: "flex", flexDirection: "column", gap: 15, borderBottom: index < tempCredentials.length - 1 ? "1px solid #27B99A" : "none", paddingBottom: 20 }}>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>License #{index + 1}</h3>

                        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                            <label style={{ fontSize: 12, fontWeight: 700 }}>State</label>
                            <div style={{ position: "relative" }}>
                                <select
                                    value={license.state}
                                    onChange={(e) => handleTempChange(index, "state", e.target.value)}
                                    style={{ width: "100%", padding: "12px 15px", borderRadius: 8, border: "none", background: "#f4f4f4", appearance: "none", fontSize: 14 }}
                                >
                                    <option value="">Select State</option>
                                    {fetchedStates.map(opt => (
                                        <option key={opt._id} value={opt.name}>
                                            {opt.name}
                                        </option>
                                    ))}
                                </select>
                                <div style={{ position: "absolute", right: 15, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                                        <path d="M1 1L6 6L11 1" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                            <label style={{ fontSize: 12, fontWeight: 700 }}>Credentials</label>
                            <div style={{ position: "relative" }}>
                                <select
                                    value={license.credentials}
                                    onChange={(e) => handleTempChange(index, "credentials", e.target.value)}
                                    style={{ width: "100%", padding: "12px 15px", borderRadius: 8, border: "none", background: "#f4f4f4", appearance: "none", fontSize: 14 }}
                                >
                                    <option value="">Select Credentials</option>
                                    {credentialsOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                </select>
                                <div style={{ position: "absolute", right: 15, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                                        <path d="M1 1L6 6L11 1" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                            <label style={{ fontSize: 12, fontWeight: 700 }}>License Number</label>
                            <input
                                type="text"
                                placeholder="1234567890"
                                value={license.licenseNumber}
                                onChange={(e) => handleTempChange(index, "licenseNumber", e.target.value)}
                                style={{ width: "100%", padding: "12px 15px", borderRadius: 8, border: "none", background: "#f4f4f4", fontSize: 14 }}
                            />
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                            <label style={{ fontSize: 12, fontWeight: 700 }}>License Expiration</label>
                            <input
                                type="date"
                                value={license.licenseExpiration}
                                onChange={(e) => handleTempChange(index, "licenseExpiration", e.target.value)}
                                style={{ width: "100%", padding: "12px 15px", borderRadius: 8, border: "none", background: "#f4f4f4", fontSize: 14 }}
                            />
                        </div>
                    </div>
                ))}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 10 }}>
                    <button
                        onClick={handleRemoveLicense}
                        style={{ padding: "8px 20px", background: "#eb5757", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 }}
                    >
                        Remove -
                    </button>
                    <button
                        onClick={handleAddLicense}
                        style={{ padding: "8px 20px", background: "#27B99A", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 }}
                    >
                        Add License +
                    </button>
                </div>

                <div style={{ display: "flex", gap: 15, marginTop: 20 }}>
                    <button
                        onClick={handleCancel}
                        style={{ flex: 1, padding: "12px", background: "#E0E0E0", color: "#333", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 700 }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        style={{ flex: 1, padding: "12px", background: "#27B99A", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 700 }}
                    >
                        Save
                    </button>
                </div>
            </div>
        );
    }

    // REVIEW MODE
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Badge Icon */}
            <div style={{ display: "flex", justifyContent: "left", marginBottom: 10 }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="81" height="80" viewBox="0 0 81 80" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M40.5 0L29.2455 10.2105L14.067 9.62099L12.0105 24.678L0 33.984L8.1 46.836L4.88249 61.686L19.35 66.3255L26.433 79.767L40.5 74.0205L54.567 79.767L61.65 66.3255L76.1175 61.686L72.9 46.836L81 33.984L68.9895 24.678L66.933 9.62099L51.75 10.2105L40.5 0ZM55.3815 26.0055L61.6185 32.4945L36.306 56.8485L19.314 39.8565L25.677 33.4935L36.432 44.244L55.3815 26.0055Z" fill="#27B99A" />
                </svg>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h2 style={{ margin: 0, fontFamily: "Poppins, sans-serif", fontWeight: 700, fontSize: 24 }}>
                    Review Credentials
                </h2>
                <button
                    onClick={() => setIsEditing(true)}
                    style={{ background: "#27B99A", color: "#fff", border: "none", borderRadius: 6, padding: "6px 20px", fontWeight: 600, cursor: "pointer", fontSize: 13 }}
                >
                    Edit
                </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 30 }}>
                {data.credentials.map((license, index) => (
                    <div key={index} style={{ borderBottom: index < data.credentials.length - 1 ? "1px solid #27B99A" : "none", paddingBottom: 25 }}>
                        <h3 style={{ margin: "0 0 15px 0", fontSize: 16, fontWeight: 700 }}>License #{index + 1}</h3>

                        <div style={{ display: "flex", flexDirection: "column", gap: 15 }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                <span style={{ fontSize: 12, fontWeight: 700 }}>State</span>
                                <div style={{ padding: "12px 15px", background: "#f4f4f4", borderRadius: 8, fontSize: 14 }}>
                                    {license.state}
                                </div>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                <span style={{ fontSize: 12, fontWeight: 700 }}>Credentials</span>
                                <div style={{ padding: "12px 15px", background: "#f4f4f4", borderRadius: 8, fontSize: 14 }}>
                                    {license.credentials}
                                </div>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                <span style={{ fontSize: 12, fontWeight: 700 }}>License Number</span>
                                <div style={{ padding: "12px 15px", background: "#f4f4f4", borderRadius: 8, fontSize: 14 }}>
                                    {license.licenseNumber}
                                </div>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                <span style={{ fontSize: 12, fontWeight: 700 }}>License Expiration</span>
                                <div style={{ padding: "12px 15px", background: "#f4f4f4", borderRadius: 8, fontSize: 14 }}>
                                    {license.licenseExpiration}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {error && <div style={{ color: "red", fontSize: 14 }}>{error}</div>}

            <button
                onClick={() => {
                    if (data.credentials.length === 0) {
                        alert("Please add at least one license");
                        return;
                    }
                    onNext();
                }}
                style={{
                    marginTop: 20,
                    padding: 0,
                    background: "none",
                    border: "none",
                    cursor: loading ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    opacity: loading ? 0.6 : 1,
                }}
                disabled={loading}
            >
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    background: loading ? "#5a8c9e" : "#07405A",
                    borderRadius: 8,
                    boxShadow: "0px 4px 12px 0px rgba(7, 64, 90, 0.10)",
                    padding: "0 24px",
                    height: 48,
                    minWidth: 160,
                    gap: 10,
                    position: "relative",
                }}>
                    <span style={{ color: "#fff", fontWeight: 700, fontSize: 16, marginRight: 24 }}>
                        {loading ? "Saving..." : "Next"}
                    </span>
                    <span style={{ position: "absolute", right: 10 }}>
                        <svg width="37" height="37" viewBox="0 0 37 37" fill="none">
                            <circle cx="18.3673" cy="18.3673" r="17.8673" fill="white" fillOpacity="0.1" stroke="white" />
                            <path d="M17.993 11.995L24.7402 18.7421L17.993 24.7396" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path opacity="0.5" d="M13 13.7257L18.2478 18.8853L13 23.4716" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </span>
                </div>
            </button>
        </div>
    );
};

export default StepReviewCredentials;
